import React, { memo, useCallback, useEffect, useState } from 'react';
import Modal from 'react-modal';
import Select from 'react-select';
import { useDebounce } from 'use-debounce';
import ReactPaginate from 'react-paginate';
import cx from 'classnames';
import {
  TASK_VIEW,
  formTableColumns,
  frequencyOptions,
  initialFiltersState,
  statusesOptions,
  taskRemoveCurrentInitiationsLabel,
  taskStopFutureInitiationsLabel,
  taskTableColumns,
  viewOptions,
} from './constants';
import {
  deleteCorporateInitiationsManagementTaskInitiation,
  getCorporateInitiationsManagementFacilities,
  getCorporateInitiationsManagementForms,
  getCorporateInitiationsManagementInitiators,
  getCorporateInitiationsManagementTaskInitiators,
  getCorporateInitiationsManagementTasks,
  stopCorporateInitiationsManagementTaskInitiation,
} from '../../../services/libraries/corporate-library/initiations-management';
import {
  deleteCorporateLibraryFormInitiation,
  removeCorporateFormManagementFormSubmissions,
  stopCorporateFormManagementFormInitiation,
} from '../../../services/libraries/corporate-library/form-management';
import { isResponseOk } from '../../../utility/isResponseOk';
import { localToUTCStringForDateRange } from '../../../utility/dates/localToUTCForDateRange';
import { utcToLocalDateFormatter } from '../../../utility/dates/utcToLocalDate';
import PageLoader from '../../other/PageLoader';
import DateCalendar from '../../calendars/DateCalendar/DateCalendar';
import { useAuth } from '../../../container/authContext';
// Reused from Form management — the per-initiation actions are identical, only the listing scope differs.
import FormSettingsModal from '../form-management-modal/components/form-settings-modal';
import StopInitiationModal from '../form-management-modal/components/stop-initiation-modal';
import DeleteInitiationModal from '../form-management-modal/components/delete-initiation-modal';
import TaskSettingsModal from './components/task-settings-modal';
import './styles.scss';

const InitiationsManagementModal = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  // Scope the listing to the currently selected company (a Super Admin can switch companies).
  const companyId = user?.company?.id;
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [initiatorsOptions, setInitiatorsOptions] = useState([]);
  const [facilitiesOptions, setFacilitiesOptions] = useState([]);
  const [filters, setFilters] = useState(initialFiltersState);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [forms, setForms] = useState(undefined);
  const [isFiltered, setIsFiltered] = useState(false);
  const [settingsTarget, setSettingsTarget] = useState(null);
  const [taskSettingsTarget, setTaskSettingsTarget] = useState(null);
  const [stopTarget, setStopTarget] = useState(null);
  const [taskStopTarget, setTaskStopTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [taskDeleteTarget, setTaskDeleteTarget] = useState(null);

  const [debouncedSearch] = useDebounce(search, 300);

  const initiationStartDate = filters?.initiationDate?.start;
  const initiationEndDate = filters?.initiationDate?.end;

  // "Task view" lists the task initiations — one row per run of a task, grouping the whole set of
  // the task's forms — instead of the initiations of a single form.
  const isTaskView = filters.view.value === TASK_VIEW;
  const tableColumns = isTaskView ? taskTableColumns : formTableColumns;

  const getInitiatedForms = async () => {
    try {
      const filteredFormStatus = filters.status.value;
      const params = { page, frequency: filters.frequency.value };

      if (companyId) {
        params['company_id'] = companyId;
      }
      if (search.length) {
        params[isTaskView ? 'name' : 'form_name'] = search;
      }
      if (filters.submitterFacilities.length) {
        // Comma-separated, the same way as the initiators below.
        params['submitter_facility_id'] = filters.submitterFacilities.map((item) => item.value).join(',');
      }
      if (filters.initiators.length) {
        // As a comma-separated list: the "in" filter of the listing reads a single value off the
        // query string, so repeated params would narrow the table down to the last initiator only.
        params['initiator_id'] = filters.initiators.map((item) => item.value).join(',');
      }
      if (initiationStartDate) {
        params['initiation_date_after'] = localToUTCStringForDateRange(initiationStartDate, 'start');
      }
      if (initiationEndDate) {
        params['initiation_date_before'] = localToUTCStringForDateRange(initiationEndDate, 'end');
      }
      if (filteredFormStatus) {
        params['status'] = filteredFormStatus;
      }

      const response = isTaskView
        ? await getCorporateInitiationsManagementTasks(params)
        : await getCorporateInitiationsManagementForms(params);

      if (isResponseOk(response)) {
        const { data: { total_pages = 1, count, results, page = 1 } = {} } = response;
        setForms(results);
        setTotalPages(total_pages);
        setTotal(count);
        setPage(page);
      }
    } catch (e) {
      console.log(e);
    }
  };

  // The two views have their own initiators, so the dropdown is refilled whenever the view changes.
  // Which view to load is passed in rather than read off the state: the switch fills the dropdown
  // for the view being opened, before the state carrying it has been applied.
  const getInitiators = async (taskView) => {
    const params = companyId ? { company_id: companyId } : undefined;
    const initiatorsRes = taskView
      ? await getCorporateInitiationsManagementTaskInitiators(params)
      : await getCorporateInitiationsManagementInitiators(params);

    if (isResponseOk(initiatorsRes)) {
      const { data } = initiatorsRes;
      if (Array.isArray(data)) {
        setInitiatorsOptions(data.map((item) => ({ value: item.initiator_id, label: item.initiator_full_name })));
      }
    }
  };

  // The facilities are the same for both views, so they are loaded once, when the popup opens.
  const getFacilities = async () => {
    const response = await getCorporateInitiationsManagementFacilities(companyId ? { company_id: companyId } : undefined);

    if (isResponseOk(response) && Array.isArray(response.data)) {
      setFacilitiesOptions(response.data.map((item) => ({ value: item.id, label: item.name })));
    }
  };

  const handleInitPage = async () => {
    try {
      setIsLoading(true);
      await getInitiatedForms();
      await Promise.all([getInitiators(isTaskView), getFacilities()]);
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  };

  const clearFormsData = () => {
    setForms(null);
    setPage(1);
    setTotalPages(0);
    setTotal(0);
  };

  // The view is what is being listed rather than how it is filtered, so "Clear All" leaves it alone —
  // closing the popup resets it along with everything else.
  const clearFilters = (keepView = true) => {
    setSearch('');
    setFilters((prevState) => ({ ...initialFiltersState, view: keepView ? prevState.view : initialFiltersState.view }));
    isFiltered && setPage(1);
    setIsFiltered(false);
  };

  useEffect(() => {
    if (isOpen) {
      handleInitPage();
    } else {
      clearFormsData();
      clearFilters(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        setIsLoading(true);
        await getInitiatedForms();
      } catch (e) {
        console.log(e);
      } finally {
        setIsLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, debouncedSearch]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setIsFiltered(true);
  };

  const handleFilter = useCallback((key, value) => {
    setFilters((prevState) => ({ ...prevState, [key]: value }));
    setIsFiltered(true);
  }, []);

  const handleViewChange = (value) => {
    // The initiators of the view left behind are not the initiators of the one being opened.
    setFilters((prevState) => ({ ...prevState, view: value, initiators: [] }));
    setPage(1);
    getInitiators(value.value === TASK_VIEW);
  };

  const handleInitiationDateChange = (date) => {
    handleFilter('initiationDate', { start: date[0], end: date[1] });
  };

  const handlePageClick = (event) => setPage(event.selected + 1);

  const closeSettingsModal = useCallback(() => setSettingsTarget(null), []);
  const closeTaskSettingsModal = useCallback(() => setTaskSettingsTarget(null), []);
  const closeStopInitiationModal = useCallback(() => setStopTarget(null), []);
  const closeTaskStopInitiationModal = useCallback(() => setTaskStopTarget(null), []);
  const closeDeleteInitiationModal = useCallback(() => setDeleteTarget(null), []);
  const closeTaskDeleteInitiationModal = useCallback(() => setTaskDeleteTarget(null), []);

  const handleStopInitiation = useCallback(
    async (stopInitiationType) => {
      if (!stopTarget) return;
      try {
        const corporateFormId = stopTarget.original_form_id;
        let response;
        if (stopInitiationType === 'stop') {
          response = await stopCorporateFormManagementFormInitiation(corporateFormId, stopTarget.id);
        } else if (stopInitiationType === 'remove') {
          response = await removeCorporateFormManagementFormSubmissions(corporateFormId, stopTarget.id);
        }

        if (isResponseOk(response)) {
          setStopTarget(null);
          await getInitiatedForms();
        }
      } catch (e) {
        console.log(e);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stopTarget],
  );

  const handleStopTaskInitiation = useCallback(
    async (stopInitiationType) => {
      if (!taskStopTarget) return;
      try {
        // A hard stop takes the forms the run already sent out out of the submitters' folders;
        // the other option only drops the initiations the run would still make.
        const response = await stopCorporateInitiationsManagementTaskInitiation(taskStopTarget.id, stopInitiationType === 'remove');

        if (isResponseOk(response)) {
          setTaskStopTarget(null);
          await getInitiatedForms();
        }
      } catch (e) {
        console.log(e);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [taskStopTarget],
  );

  const handleDeleteInitiation = useCallback(async () => {
    if (!deleteTarget) return;
    setIsLoading(true);
    const response = await deleteCorporateLibraryFormInitiation(deleteTarget.original_form_id, deleteTarget.id);
    if (isResponseOk(response)) {
      setDeleteTarget(null);
      await getInitiatedForms();
    }
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteTarget]);

  const handleDeleteTaskInitiation = useCallback(async () => {
    if (!taskDeleteTarget) return;
    setIsLoading(true);
    // The run goes for good, with the forms it sent out and every submission made on them.
    const response = await deleteCorporateInitiationsManagementTaskInitiation(taskDeleteTarget.id);
    if (isResponseOk(response)) {
      setTaskDeleteTarget(null);
      await getInitiatedForms();
    }
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskDeleteTarget]);

  const renderSearch = () => {
    return (
      <div className={'search-row'}>
        <div className={'search-input-wrapper'}>
          <input id="search" value={search} onChange={handleSearch} type="text" className="form-control" placeholder="Search by name" />
          <i className={'icon-search'} />
        </div>
        <div className={'submitters-from'}>
          <span className="label">Submitters from</span>
          {/* Nothing selected is "All": the listing is not narrowed down by the submitters' facility. */}
          <Select
            isMulti
            placeholder="All"
            options={facilitiesOptions}
            value={filters.submitterFacilities}
            onChange={(e) => handleFilter('submitterFacilities', e || [])}
            className="form-control-select filter"
            classNamePrefix="react-select-multi"
          />
        </div>
      </div>
    );
  };

  const renderFilters = () => {
    return (
      <div className={'filters'}>
        <div className={'filters-block'}>
          <span className="label">Initiator</span>
          <Select
            isMulti
            options={initiatorsOptions}
            value={filters.initiators}
            onChange={(e) => handleFilter('initiators', e)}
            className="form-control-select filter"
            classNamePrefix="react-select-multi"
          />
        </div>
        <div className={'filters-block'}>
          <span className="label">Initiation date (period)</span>
          <DateCalendar
            isClearable
            selectsRange
            selectedDate={initiationStartDate}
            startDate={initiationStartDate}
            endDate={initiationEndDate}
            placeholderText="Select Date"
            handleChange={handleInitiationDateChange}
          />
        </div>
        <div className={'filters-block'}>
          <span className="label">Status</span>
          <Select
            value={filters.status}
            options={statusesOptions}
            classNamePrefix="react-select"
            className="form-control-select filter"
            onChange={(e) => handleFilter('status', e)}
          />
        </div>
        <div className={'filters-block'}>
          <span className="label">Frequency</span>
          <Select
            value={filters.frequency}
            options={frequencyOptions}
            classNamePrefix="react-select"
            className="form-control-select filter"
            onChange={(e) => handleFilter('frequency', e)}
          />
        </div>
        <div className={'filters-block'}>
          <span className="label">View</span>
          <Select
            value={filters.view}
            options={viewOptions}
            classNamePrefix="react-select"
            className="form-control-select filter"
            onChange={handleViewChange}
          />
        </div>
        <div className={'filters-block'}>
          <button className="btn btn-outline clear-filters-btn" onClick={() => clearFilters()}>
            Clear All
          </button>
        </div>
      </div>
    );
  };

  const renderNextInitiation = (nextInitiation) => {
    if (!nextInitiation) return '-';
    const date = utcToLocalDateFormatter(nextInitiation, 'MMMM D,');
    const time = utcToLocalDateFormatter(nextInitiation, 'h:mm A');
    if (!date) return '-';
    return (
      <span className="next-initiation">
        {date}
        <br />
        {time}
      </span>
    );
  };

  // A stopped initiation has no next initiation left, so the column says when — and by whom — it
  // was stopped instead, the same way the Form management popup does.
  const renderStopped = (stoppedAt, stoppedBy) => {
    const date = stoppedAt ? utcToLocalDateFormatter(stoppedAt) : '';
    return <span className="stopped-info">{['Stopped', date, stoppedBy && `by ${stoppedBy}`].filter(Boolean).join(' ')}</span>;
  };

  const renderRowActions = (item) => {
    // A task initiation is run from the Tasks page, so it carries the settings of the run, the stop
    // and the delete — stopping or deleting one of its forms on its own would break the set the task groups.
    if (isTaskView) {
      return (
        <div className="table-row-actions">
          <button className="action-btn settings-btn" title="View settings" onClick={() => setTaskSettingsTarget(item.id)}>
            <i className="icon-settings" />
          </button>
          {!item.stopped_at && (
            <button className="action-btn stop-btn" title="Stop the initiation" onClick={() => setTaskStopTarget(item)}>
              <i className="icon-icon-x" />
            </button>
          )}
          <button className="action-btn delete-btn" title="Delete initiation" onClick={() => setTaskDeleteTarget(item)}>
            <i className="icon-remove" />
          </button>
        </div>
      );
    }

    const { stopped_at, can_delete_initiations, task_initiation_id } = item;
    // An initiation a task run made is part of the set of forms the run groups, so it is stopped
    // together with the run, from the task view — never stopped or deleted on its own.
    const isFromTask = Boolean(task_initiation_id);
    const canDelete = can_delete_initiations !== false && !isFromTask;
    return (
      <div className="table-row-actions">
        <button
          className="action-btn settings-btn"
          title="View settings"
          onClick={() => setSettingsTarget({ corporateFormId: item.original_form_id, initiatedFormId: item.id })}
        >
          <i className="icon-settings" />
        </button>
        {isFromTask && (
          <button className="action-btn settings-btn" title="View task settings" onClick={() => setTaskSettingsTarget(task_initiation_id)}>
            <i className="icon-icon-list" />
          </button>
        )}
        {!stopped_at && !isFromTask && (
          <button className="action-btn stop-btn" title="Stop the initiation" onClick={() => setStopTarget(item)}>
            <i className="icon-icon-x" />
          </button>
        )}
        {canDelete && (
          <button className="action-btn delete-btn" title="Delete initiation" onClick={() => setDeleteTarget(item)}>
            <i className="icon-remove" />
          </button>
        )}
      </div>
    );
  };

  const renderFormsTable = () => {
    return (
      <div className="table-responsive table-default table-full table-forms">
        <div className="table-wrapper">
          {forms?.length ? (
            <div className="table">
              <div className="tr">
                {tableColumns.map((item) => {
                  const { label, key } = item;
                  return (
                    <div className="th" key={`table-th-${key}`} style={{ textAlign: key !== 'name' ? 'center' : undefined }}>
                      {label}
                      {key === 'name' && !!total && <span className="th-total">{`(Total: ${total})`}</span>}
                    </div>
                  );
                })}
              </div>
              {forms.map((item) => {
                const {
                  id,
                  form_name,
                  task_name,
                  task_initiation_id,
                  initiation_date,
                  initiator_full_name,
                  next_initiation,
                  frequency,
                  stopped_at,
                  stopped_by,
                } = item;
                const isStopped = Boolean(stopped_at || stopped_by);

                return (
                  <div key={id} className={cx('tr', { stopped: isStopped })}>
                    <div className="td">
                      <p
                        style={{
                          maxWidth: 360,
                          display: 'inline-block',
                          color: 'var(--Secondary-eading, #101828)',
                        }}
                      >
                        {isTaskView ? task_name : form_name}
                      </p>
                      {/* In the form view, the initiations a task run made are told apart from the ones of the form alone. */}
                      {!isTaskView && task_initiation_id && (
                        <span className="task-label" title={task_name ? `Initiated by the task "${task_name}"` : 'Initiated by a task'}>
                          {task_name ? `Task: ${task_name}` : 'Task'}
                        </span>
                      )}
                    </div>
                    <div className="td">{initiator_full_name || '-'}</div>
                    <div className="td">{initiation_date ? utcToLocalDateFormatter(initiation_date) : '-'}</div>
                    <div className="td">{isStopped ? renderStopped(stopped_at, stopped_by) : renderNextInitiation(next_initiation)}</div>
                    <div className="td">
                      <span className="frequency">{frequency || '-'}</span>
                    </div>
                    <div className="td">{renderRowActions(item)}</div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-page">
              <h3 className="empty-page-title">{isFiltered ? 'Does not match any results!' : !isLoading && 'Data not found'}</h3>
            </div>
          )}

          {!!forms?.length && !!totalPages && (
            <ReactPaginate
              breakLabel="..."
              nextLabel="next ->"
              forcePage={page - 1}
              pageRangeDisplayed={5}
              pageCount={totalPages}
              previousLabel="<- previous"
              className={'react-pagination'}
              onPageChange={handlePageClick}
            />
          )}
          {isLoading && <PageLoader />}
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <>
      <Modal
        id={'initiationsManagementModal'}
        className="modal-default initiations-management-modal"
        overlayClassName={'initiations-management-modal-overlay'}
        isOpen={true}
        onRequestClose={onClose}
        contentLabel="Initiations management"
        ariaHideApp={false}
      >
        <h2 className="modal-header">
          {'Initiations management'}
          <button className="btn btn-icon" onClick={onClose}>
            <i className="icon-icon-x" />
          </button>
        </h2>
        <div className="modal-body">
          {renderSearch()}
          {renderFilters()}
          {renderFormsTable()}
        </div>
      </Modal>

      <FormSettingsModal
        formId={settingsTarget?.corporateFormId}
        settingsFormId={settingsTarget?.initiatedFormId}
        onClose={closeSettingsModal}
        setIsLoading={setIsLoading}
      />
      <TaskSettingsModal taskInitiationId={taskSettingsTarget} onClose={closeTaskSettingsModal} setIsLoading={setIsLoading} />
      <StopInitiationModal
        isOpen={!!stopTarget}
        onClose={closeStopInitiationModal}
        hasFrequencySettings={stopTarget?.has_frequency_settings}
        onStopInitiation={handleStopInitiation}
      />
      <StopInitiationModal
        isOpen={!!taskStopTarget}
        onClose={closeTaskStopInitiationModal}
        // Only a run with a frequency has future initiations to stop: a one-off run — immediate or
        // scheduled for a specific time — can only be removed outright.
        hasFrequencySettings={taskStopTarget?.has_frequency_settings}
        onStopInitiation={handleStopTaskInitiation}
        stopFutureInitiationsLabel={taskStopFutureInitiationsLabel}
        removeCurrentInitiationsLabel={taskRemoveCurrentInitiationsLabel}
      />
      <DeleteInitiationModal isOpen={!!deleteTarget} onDeleteInitiation={handleDeleteInitiation} onClose={closeDeleteInitiationModal} />
      <DeleteInitiationModal
        isOpen={!!taskDeleteTarget}
        onDeleteInitiation={handleDeleteTaskInitiation}
        onClose={closeTaskDeleteInitiationModal}
        content={
          <>
            Are you sure you want to delete this task initiation? <br />
            All its submissions will be permanently deleted.
          </>
        }
      />
    </>
  );
};

export default memo(InitiationsManagementModal);
